// © Microsoft Corporation. All rights reserved.

import { ConfigOrigin, ConfigOrigins, ResolverEnvironment } from "@msnews/core-contracts";
import { ConfigReference, ExperienceConfig, ExperienceConfigLink, PageRequestTargetScope } from "@msnews/config-data-types";
import { featureConfigs, featureManifests, localizationConfigs, localizationManifests } from "../../utilities/Constants";
import { getBuildNumberAsync, getConfigReferencePath } from "../../utilities/Utilities";

import { ConfigIndexService } from "./ConfigIndexService";
import { ConfigService } from "./ConfigService";
import { ManifestService } from "./ManifestService";
import { mergeConfigs } from "./ConfigMerger";
import { targetScopeMatched } from "./TargetScopeMatcher";

/**
 * Manifest Service class
 * This is for fetching and parsing the Manifest into an easily consumable form.
 */
export class SingleConfigResolver {
    /** Application Type */
    private appType: string;

    /** Target Scope */
    private targetScope: PageRequestTargetScope;

    /** Build Version */
    private buildVersion: string;

    /** Manifest Service */
    private manifestService: ManifestService;

    /** Config Index Service */
    private configIndexService: ConfigIndexService;

    /** Config Service */
    private configService: ConfigService;

    /** Indicate if configs should be served from local repo */
    private configOrigins: ConfigOrigins;

    /** Build number associated with app type */
    private _buildNumber: string;

    /**
     * Initialize single config resolver based on fetching parameters.
     * @param {ResolverEnvironment} resolverEnvironment
     */
    constructor(resolverEnvironment: ResolverEnvironment) {
        if (!resolverEnvironment
            || !resolverEnvironment.configServiceParameters
            || !resolverEnvironment.appType
            || !resolverEnvironment.configServiceParameters.configServiceBaseUrl
            || !resolverEnvironment.targetScope) {
            throw new Error("SingleConfigResolver.ts: ResolverEnvironment or configServiceParameters or targetScope does not exist.");
        }

        this.appType = resolverEnvironment.appType;
        this.targetScope = resolverEnvironment.targetScope;
        this.buildVersion = resolverEnvironment.configServiceParameters.buildVersion;
        this.configOrigins = resolverEnvironment.configOrigins;

        this.manifestService = new ManifestService(this.appType, this.configOrigins);
        this.configIndexService = new ConfigIndexService(this.appType, this.configOrigins);
        this.configService = new ConfigService(this.appType, this.configOrigins);
    }

    /**
     * This function fetches and parses manifest file from azure blob storage.
     * @param {string} configType config type, loc-configs or feature-configs
     * @param {string} appType application type
     * @param {string} version build version
     */
    public async resolveConfigAsync(entity: ConfigReference): Promise<ExperienceConfig> {
        // todo: consider using promise.then.then train to avoid awaiting for individual steps.
        const featureEntityReference = getConfigReferencePath(entity);
        const localizationEntityReference = getConfigReferencePath(entity, true);

        this.buildNumber = this.tryGetBuildNumber() || await getBuildNumberAsync(this.buildVersion, this.appType);

        // fetch the manifest files
        const featureManifestTask = this.manifestService.fetchAndParseManifest(featureManifests, this.buildNumber);
        const localizationManifestTask = this.manifestService.fetchAndParseManifest(localizationManifests, this.buildNumber);

        // wait for manifest fetches, they are used within ManifestService so no return
        await Promise.all([featureManifestTask, localizationManifestTask]);

        // try to find configIndexReference in manifest file
        const featureIndexHashedFileName = this.manifestService.getConfigIndexHashedFileName(featureEntityReference, featureManifests);
        const localizationIndexHashedFileName = this.manifestService.getConfigIndexHashedFileName(localizationEntityReference, localizationManifests);

        // try to get config index files
        const featureConfigIndexTask = this.configIndexService.fetchAndParseConfigIndex(featureConfigs, featureEntityReference, featureIndexHashedFileName);
        const localizationConfigIndexTask = this.configIndexService.fetchAndParseConfigIndex(localizationConfigs, localizationEntityReference, localizationIndexHashedFileName);

        const indexResults = await Promise.all([featureConfigIndexTask, localizationConfigIndexTask]).then((results) => {
            return {
                featureIndex: results[0],
                localizationIndex: results[1]
            };
        });

        // maybe skip these just by returning .configs of index, since its default
        const featureConfigPaths = indexResults.featureIndex && indexResults.featureIndex.configs || undefined;
        const localizationConfigPaths = indexResults.localizationIndex && indexResults.localizationIndex.configs || undefined;

        const configTasks: Promise<ExperienceConfig>[] = [];
        // get the config tasks according to list of config paths from the index
        this.fetchMathingConfigs(configTasks, featureConfigPaths, featureManifests, featureConfigs, featureEntityReference);
        this.fetchMathingConfigs(configTasks, localizationConfigPaths, localizationManifests, localizationConfigs, localizationEntityReference);
        if (configTasks.length == 0) {
            return undefined;
        }

        // await all the configs and merge them
        const configsToMerge = await Promise.all(configTasks).then(results => results);
        const filteredConfigs = configsToMerge.filter(config => config != null);    // Remove null or undefined configs
        const mergedConfig = mergeConfigs(filteredConfigs);

        return mergedConfig;
    }

    /**
     * Checks if the config paths match the targetscope of the page.
     * If it matches, starts config fetch task and adds it to the configTasks input parameter.
     * @param {Promise<ExperienceConfig>[]} configTasks array consisting of config fetching tasks, will be updated in this function
     * @param {ExperienceConfigLink[]} configPaths config paths from the config index file
     * @param {string} manifestType config type, loc-manifests or feature-manifests
     * @param {string} configType config type, loc-configs or feature-configs
     * @param {string} configReference configReference to use when getting the hashed file name
     */
    private fetchMathingConfigs(configTasks: Promise<ExperienceConfig>[], configPaths: ExperienceConfigLink[], manifestType: string, configType: string, configReference: string): void {
        if (!configPaths || configPaths.length == 0) {
            return;
        }

        configPaths.forEach(configPath => {
            if (targetScopeMatched(configPath.targetScope, this.targetScope)) {
                const configFileName = configPath.src;
                const hashedFileName = this.manifestService.getConfigHashedFileName(configReference, configFileName, manifestType);
                configTasks.push(this.configService.fetchAndParseConfig(configType, configReference, configFileName, hashedFileName));
            }
        });
    }

    /**
     * Returns all failed fetches for throwing errors.
     */
    public getFailedFetches(): string[] {
        const allNullFetches: string[] = [];
        const configNullFetches = this.configService.getFailedConfigFetches();
        if (configNullFetches.length > 0) {
            allNullFetches.push(...configNullFetches);
        }
        const configIndexNullFetches = this.configIndexService.getFailedConfigIndexFetches();
        if (configIndexNullFetches.length > 0) {
            allNullFetches.push(...configIndexNullFetches);
        }
        return allNullFetches;
    }

    public get buildNumber(): string { return this._buildNumber; }
    public set buildNumber(value: string) { this._buildNumber = value; }

    /**
     * Returns build number if already stored or in prod scenario and returns null for local dev scenario
     * (where feature configs are from local repo but localized configs are from PROD (blob storage)
     * so that it can be fetched asynchronously later on
     */
    private tryGetBuildNumber(): string {
        if (this.buildNumber) {
            return this.buildNumber;    // If it has already been set, return it
        } else if (this.configOrigins.feature === ConfigOrigin.Local && this.configOrigins.loc === ConfigOrigin.Production) {
            // Return null so build number can be fetched asynchronously later
            // Fetch build number look up *only* for feature configs local and localized configs from PROD blob storage
            return null;
        } else {    // PROD scenario
            return this.buildVersion;   // First time look up for prod scenario.  After this first lookup, stored in buildNumber
        }
    }
}
